import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Sparkles,
  Landmark,
  LineChart,
  CalendarRange,
  Brain,
  Calculator,
  ClipboardList,
  ShieldCheck,
  Activity,
} from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";

const SITE = "https://xauproacademy.com";
const PAGE_URL = `${SITE}/platform`;

export const Route = createFileRoute("/platform/")({
  head: () => ({
    meta: [
      { title: "XAU Pro Platform: Institutional Gold Analysis and Execution Suite" },
      {
        name: "description",
        content:
          "Free institutional gold tools (Gold COT Report, US CPI and Gold, Gold Seasonality, StructureLens) plus the members environment: AI Chart Analyst, risk calculator, and graded SMC execution setups.",
      },
      {
        property: "og:title",
        content: "XAU Pro Platform: Institutional Gold Analysis and Execution Suite",
      },
      {
        property: "og:description",
        content:
          "Institutional gold analysis tools and a members environment built on the Orion decision engine.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
  }),
  component: PlatformPage,
});

type ToolStatus = "available" | "members";

type ToolRoute =
  | "/platform/structurelens"
  | "/gold-cot-report"
  | "/gold-inflation-cpi"
  | "/gold-seasonality"
  | "/dashboard/analyst"
  | "/dashboard/tools"
  | "/signals";

type Tool = {
  name: string;
  eyebrow: string;
  description: string;
  status: ToolStatus;
  icon: typeof Sparkles;
  to: ToolRoute;
};


const freeTools: Tool[] = [
  {
    name: "StructureLens",
    eyebrow: "Chart analysis",
    icon: Sparkles,
    status: "available",
    to: "/platform/structurelens",
    description:
      "Institutional SMC chart analysis on demand. Upload a chart, receive a structured reading grounded in market structure, liquidity, and displacement.",
  },
  {
    name: "Gold COT Report",
    eyebrow: "Weekly positioning",
    icon: Landmark,
    status: "available",
    to: "/gold-cot-report",
    description:
      "What funds, banks, and producers did in COMEX gold this week, straight from the official CFTC report and read in plain terms.",
  },
  {
    name: "US CPI and Gold",
    eyebrow: "Monthly inflation",
    icon: LineChart,
    status: "available",
    to: "/gold-inflation-cpi",
    description:
      "Official US inflation readings next to gold's monthly move, with release context. Built on the BLS data series, not summaries.",
  },
  {
    name: "Gold Seasonality",
    eyebrow: "Monthly tendency",
    icon: CalendarRange,
    status: "available",
    to: "/gold-seasonality",
    description:
      "How XAUUSD performed in each calendar month over the last 10 years, month by month, from spot history.",
  },
];

const memberTools: Tool[] = [
  {
    name: "AI Chart Analyst",
    eyebrow: "Analysis intelligence",
    icon: Brain,
    status: "members",
    to: "/dashboard/analyst",
    description:
      "Upload any XAUUSD chart and receive an SMC and ICT read with bias, POI, and reasoning on demand, on top of the free StructureLens flow.",
  },
  {
    name: "Risk Calculator",
    eyebrow: "Risk engine",
    icon: Calculator,
    status: "members",
    to: "/dashboard/tools",
    description:
      "Position sizing aware of funded-account drawdown rules. Volatility-respecting stop placement, not blanket percentage risk.",
  },
  {
    name: "SMC Execution Setups",
    eyebrow: "Signals desk",
    icon: ClipboardList,
    status: "members",
    to: "/signals",
    description:
      "Graded A+ and A setups grounded in liquidity, displacement, and structure confirmation, with full SL and TP reasoning. Research output, not tips.",
  },
];

function StatusPill({ status }: { status: ToolStatus }) {
  const map = {
    available: { label: "Free to use", cls: "border-gold/40 text-gold" },
    members: { label: "Members area", cls: "border-border/70 text-foreground/80" },
  } as const;
  const m = map[status];
  return (
    <span
      className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.2em] ${m.cls}`}
    >
      {m.label}
    </span>
  );
}

function ToolCard({ tool }: { tool: Tool }) {
  const Icon = tool.icon;
  const inner = (
    <>
      <div className="flex items-start justify-between gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-md border border-border/60 bg-background/50">
          <Icon className="h-5 w-5 text-gold" />
        </div>
        <StatusPill status={tool.status} />
      </div>
      <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        {tool.eyebrow}
      </div>
      <div className="mt-1 font-display text-xl font-semibold text-foreground">
        {tool.name}
      </div>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        {tool.description}
      </p>
      <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-gold">
        {tool.status === "available" ? "Open the tool" : "Open in members area"}{" "}
        <ArrowRight className="h-3.5 w-3.5" />
      </div>
    </>
  );
  const cls =
    "group rounded-xl border border-border/60 bg-background/40 p-6 transition hover:border-gold/40 hover:bg-background/70";
  return (
    <Link key={tool.name} to={tool.to} className={cls}>
      {inner}
    </Link>
  );
}

function PlatformPage() {
  const { user } = useAuth();
  const primaryTo = user ? "/dashboard" : "/auth";
  const primaryLabel = user ? "Open Dashboard" : "Start Free";

  return (
    <Layout>
      {/* Hero */}
      <section className="border-b border-border/60 bg-background">
        <div className="container mx-auto max-w-6xl px-4 py-20 lg:py-28">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
            Platform
          </div>
          <h1 className="mt-3 max-w-4xl font-display text-4xl font-semibold tracking-tight text-foreground lg:text-6xl">
            Institutional gold analysis and execution, on one surface.
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground lg:text-lg">
            Free macro and structure tools on the open web. The members
            environment adds AI chart analysis, risk sizing, and graded SMC
            execution setups, downstream of every Orion decision.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild variant="gold" size="lg">
              <Link
                to={primaryTo}
                {...(!user ? { search: { tab: "signup" } } : {})}
              >
                {primaryLabel} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/gold-cot-report">Explore the Free Tools</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Free tools */}
      <section className="border-b border-border/60">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <div className="flex items-end justify-between gap-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                Open tools
              </div>
              <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
                Free institutional data tools. No account required.
              </h2>
              <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
                Built on official datasets (CFTC, BLS, spot history) and served
                with a short cache, so the numbers you read are the numbers the
                source published.
              </p>
            </div>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {freeTools.map((tool) => (
              <ToolCard key={tool.name} tool={tool} />
            ))}
          </div>
        </div>
      </section>

      {/* Members environment */}
      <section className="border-b border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <div className="flex items-end justify-between gap-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                Members environment
              </div>
              <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
                The execution layer for subscribers.
              </h2>
              <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
                The same institutional philosophy, applied to your workflow.
                Analysis, sizing, and graded setups in one place, inside the
                members area.
              </p>
            </div>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {memberTools.map((tool) => (
              <ToolCard key={tool.name} tool={tool} />
            ))}
          </div>
        </div>
      </section>

      {/* Trust chain band */}
      <section className="border-b border-border/60">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            How it fits together
          </div>
          <h2 className="mt-2 max-w-3xl font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
            Decision, operation, evidence.
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Platform does not exist in isolation. It is the middle link in a
            three-part chain that defines how this desk operates.
          </p>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            <Link
              to="/orion"
              className="group rounded-xl border border-border/60 bg-background/40 p-6 transition hover:border-gold/40"
            >
              <Brain className="h-5 w-5 text-gold" />
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Intelligence
              </div>
              <div className="mt-1 font-display text-lg font-semibold text-foreground">
                Orion produces the decision.
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                Liquidity, structure, displacement, session, macro. Graded,
                reasoned, logged.
              </div>
            </Link>
            <div className="rounded-xl border border-gold/50 bg-background/40 p-6">
              <Activity className="h-5 w-5 text-gold" />
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
                Platform
              </div>
              <div className="mt-1 font-display text-lg font-semibold text-foreground">
                You operationalize it.
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                Free data tools, analyst, risk engine, and graded setups. The
                trader-facing execution layer.
              </div>
            </div>
            <Link
              to="/validation"
              className="group rounded-xl border border-border/60 bg-background/40 p-6 transition hover:border-gold/40"
            >
              <ShieldCheck className="h-5 w-5 text-gold" />
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Evidence
              </div>
              <div className="mt-1 font-display text-lg font-semibold text-foreground">
                Validation proves it worked.
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                Methodology, shadow vs live, full audits. Open evidence, not
                marketing screenshots.
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section>
        <div className="container mx-auto max-w-4xl px-4 py-20 text-center">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground lg:text-4xl">
            Operate inside the system.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground">
            Membership opens the members environment to your account. Analysis,
            sizing, and graded setups in one place.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild variant="gold" size="lg">
              <Link to="/pricing">View Membership</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/orion">Read about Orion</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
